class CartRecommendations extends HTMLElement {
  // BSF: Static debounce timer for reloadAll
  static _reloadTimeout = null;

  constructor() {
    super();
    this.currentProductId = null; // BSF: Track current product for optimization
    this.observer = null; // BSF: Store observer reference for cleanup
    this.abortController = null; // BSF: Cancel stale requests
  }

  connectedCallback() {
    // BSF: Delay IntersectionObserver setup to avoid race condition
    // When element is recreated during cart update, reloadAll() runs after 100ms
    // We wait 150ms so reloadAll() can populate _cartProductIds first
    setTimeout(() => {
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this));
      this.observer.observe(this);
    }, 150);
  }

  disconnectedCallback() {
    // BSF: Clean up observer to prevent memory leaks
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    // BSF: Cancel any pending requests
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    this.loadRecommendations();
  }

  loadRecommendations(force = false) {
    // BSF: Skip if same product (unless forced)
    const urlProductId = this.getProductIdFromUrl();
    if (!force && urlProductId && urlProductId === this.currentProductId) {
      return;
    }
    this.currentProductId = urlProductId;

    // BSF: Cancel any pending request to prevent race conditions
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // BSF: ALWAYS fetch cart.json to ensure we have the absolute latest cart data
    // This eliminates ALL timing/race conditions when adding products from recommendations
    const cartUrl = (window.Shopify?.routes?.root || '/') + 'cart.json';

    fetch(cartUrl, { signal })
      .then(r => {
        if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
        return r.json();
      })
      .then(cartData => {
        // Store fresh cart data - use BOTH product ID and handle for matching
        if (cartData.items && cartData.items.length > 0) {
          CartRecommendations._cartProductIds = new Set(cartData.items.map(item => String(item.product_id)));
          CartRecommendations._cartProductHandles = new Set(cartData.items.map(item => item.handle));
        } else {
          CartRecommendations._cartProductIds = new Set();
          CartRecommendations._cartProductHandles = new Set();
        }
        // Now fetch recommendations
        if (!this.dataset.url) {
          return null;
        }
        return fetch(this.dataset.url, { signal });
      })
      .then((response) => {
        if (!response) return null;
        return response.text();
      })
      .then((text) => {
        if (!text) return; // No recommendations to process
        const html = document.createElement("div");
        html.innerHTML = text;
        // BSF: Client-side filter to remove products already in cart
        this.filterCartItems(html);
        const recommendations = html.querySelector("cart-recommendations");
        const cartRecommendationsList = html.querySelector(".cart-recommendations--list");
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          const container = this.closest(".cart--recommendations");
          if (container) {
            if (!cartRecommendationsList || !cartRecommendationsList.innerHTML.trim().length) {
              container.classList.add("hidden");
            } else {
              container.classList.remove("hidden");
            }
          }
        }
        this.initQtySelects();
      })
      .catch((e) => {
        // BSF: Silently ignore aborted requests (expected during rapid updates)
        if (e.name === 'AbortError') return;
      });
  }

  initQtySelects() {
    // Handle native quantity select in recommendations
    this.querySelectorAll('[data-bsf-qty-select]').forEach((select) => {
      // BSF: Prevent duplicate listeners
      if (select.dataset.qtyInit) return;
      select.dataset.qtyInit = 'true';

      const form = select.closest('form');
      if (!form) return;

      // On select change, submit the form
      select.addEventListener('change', () => {
        if (select.value) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    });
  }

  updateUrl(productId) {
    if (productId) {
      const { routes } = window.MinimogSettings;
      this.dataset.url = `${routes.product_recommendations_url}?section_id=cart-drawer&product_id=${productId}&limit=8`;
    }
  }

  // BSF: Extract product ID from data-url for comparison
  getProductIdFromUrl() {
    const url = this.dataset.url || '';
    const match = url.match(/product_id=(\d+)/);
    return match ? match[1] : null;
  }

  // BSF: Get product IDs currently in cart
  // Uses stored cart items from API if available (more reliable), falls back to DOM query
  static getCartProductIds() {
    // Use stored cart items from API response if available (avoids DOM timing issues)
    if (CartRecommendations._cartProductIds && CartRecommendations._cartProductIds.size > 0) {
      return CartRecommendations._cartProductIds;
    }

    // Fallback: query DOM
    const cartItems = document.querySelectorAll('.m-cart-drawer-item[data-product-id]');
    const ids = new Set();
    cartItems.forEach(item => {
      const rawId = item.getAttribute('data-product-id');
      const productId = rawId ? rawId.trim() : null;
      if (productId) ids.add(productId);
    });
    return ids;
  }

  // BSF: Filter out products that are in cart from recommendations HTML
  filterCartItems(html) {
    const cartProductIds = CartRecommendations.getCartProductIds();
    if (cartProductIds.size === 0) return;

    // Find all recommendation list items and filter out cart products
    const list = html.querySelector('.cart-recommendations--list');
    if (!list) return;

    const cartHandles = CartRecommendations._cartProductHandles || new Set();

    // BSF: Collect items to remove first, then batch remove (avoids reflow during iteration)
    const toRemove = [];
    list.querySelectorAll('li').forEach(li => {
      const productCard = li.querySelector('[data-product-id]');
      const productLink = li.querySelector('a[href*="/products/"]');

      // Get product ID
      const rawId = productCard ? productCard.getAttribute('data-product-id') : null;
      const recId = rawId ? rawId.trim() : null;

      // Get product handle from URL
      const href = productLink ? productLink.getAttribute('href') : '';
      const handleMatch = href.match(/\/products\/([^?/]+)/);
      const recHandle = handleMatch ? handleMatch[1] : null;

      const inCartById = cartProductIds.has(recId);
      const inCartByHandle = recHandle && cartHandles.has(recHandle);

      if (inCartById || inCartByHandle) {
        toRemove.push(li);
      }
    });

    // BSF: Batch remove to minimize reflows
    toRemove.forEach(li => li.remove());
  }

  // BSF: Update content from pre-fetched HTML (used by unified reload)
  updateFromHtml(html) {
    const recommendations = html.querySelector("cart-recommendations");
    const cartRecommendationsList = html.querySelector(".cart-recommendations--list");
    if (recommendations && recommendations.innerHTML.trim().length) {
      this.innerHTML = recommendations.innerHTML;
      const container = this.closest(".cart--recommendations");
      if (container) {
        if (!cartRecommendationsList || !cartRecommendationsList.innerHTML.trim().length) {
          container.classList.add("hidden");
        } else {
          container.classList.remove("hidden");
        }
      }
    }
    this.initQtySelects();
  }

  // BSF: Static method for unified reload - updates URL and triggers loadRecommendations on each element
  // Debounced to prevent API floods from rapid cart updates
  static reloadAll(productId) {
    // BSF: Immediately update cached cart IDs so any concurrent filtering knows about the new product
    if (productId) {
      if (!CartRecommendations._cartProductIds) {
        CartRecommendations._cartProductIds = new Set();
      }
      CartRecommendations._cartProductIds.add(String(productId));
    }

    // BSF: Cancel any pending reload
    if (CartRecommendations._reloadTimeout) {
      clearTimeout(CartRecommendations._reloadTimeout);
    }

    // BSF: Store productId for debounced execution
    CartRecommendations._pendingProductId = productId;

    // BSF: Debounce 200ms - fast enough for good UX, prevents API floods
    CartRecommendations._reloadTimeout = setTimeout(() => {
      const pid = CartRecommendations._pendingProductId;
      // Clear stale cart data - loadRecommendations() will fetch fresh from cart.json
      CartRecommendations._cartProductIds = null;

      document.querySelectorAll('cart-recommendations').forEach(el => {
        // Update URL with new product ID
        if (pid) {
          el.updateUrl(pid);
        }
        // Reset currentProductId to force reload even if same product
        el.currentProductId = null;
        // Force reload - will fetch fresh cart.json and recommendations
        el.loadRecommendations(true);
      });
    }, 200);
  }
}
customElements.define("cart-recommendations", CartRecommendations);

// BSF: Expose class globally so other scripts can call reloadAll()
window.CartRecommendations = CartRecommendations;
